/**
 * patientTags.service.ts — Admin > Patient Tags: the account's custom patient
 * tags (tags_custom), edited through /v5/tags-custom as the corporate dashboard
 * does, and the shared icon library they pick from (/v5/tag-icons).
 *
 *   - A delete sets the tag inactive; restore is an update back to active
 *     (there is no restore route). Writes need Admin Access.
 *   - Icons live on the server's file store, not a public URL: an icon is read
 *     as base64 JSON and shown through an object URL, so the bearer token never
 *     goes into an <img src>. (Tags already on patients use hooks/useTagIcon,
 *     which reads a tag's icon by tag id; the picker needs any icon by path.)
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminPatientTag {
  tagsCustomID: number;
  title: string;
  /** Icon path in the library, e.g. "Tags/tag1.png"; '' for none. */
  icon: string;
  urlTarget: boolean;
  adminAdd: boolean;
  adminRemove: boolean;
  active: boolean;
}

export interface PatientTagPayload {
  title: string;
  icon: string;
  urlTarget: boolean;
  adminAdd: boolean;
  adminRemove: boolean;
}

export interface TagIconCategory {
  category: string;
  icons: { file: string; path: string }[];
}

/** Corp's starting icon for a new tag. */
export const DEFAULT_TAG_ICON = 'Tags/tag1.png';

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

/** Every tag, deleted ones too, by title. */
export function listAdminPatientTags(): Promise<AdminPatientTag[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/tags-custom').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => ({
      tagsCustomID: Number(row.tagsCustomID ?? 0),
      title: String(row.title ?? ''),
      icon: String(row.icon ?? '').trim(),
      urlTarget: flag(row.urlTarget),
      adminAdd: flag(row.adminAdd),
      adminRemove: flag(row.adminRemove),
      active: flag(row.active),
    })),
  );
}

export function createPatientTag(data: PatientTagPayload): Promise<void> {
  return apiClient.post('/tags-custom', { ...data, active: true }).then(() => undefined);
}

export function updatePatientTag(id: number, data: Partial<PatientTagPayload> & { active?: boolean }): Promise<void> {
  return apiClient.put(`/tags-custom/${id}`, data).then(() => undefined);
}

export function deletePatientTag(id: number): Promise<void> {
  return apiClient.delete(`/tags-custom/${id}`).then(() => undefined);
}

export function restorePatientTag(id: number): Promise<void> {
  return updatePatientTag(id, { active: true });
}

/** The shared icon library, by category. */
export function listTagIconCatalog(): Promise<TagIconCategory[]> {
  return apiClient.get<ApiResponse<TagIconCategory[]>>('/tag-icons').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : []).map((c) => ({
      category: String(c.category ?? ''),
      icons: Array.isArray(c.icons) ? c.icons.map((i) => ({ file: String(i.file ?? ''), path: String(i.path ?? '') })) : [],
    })),
  );
}

/** One library icon as an object URL, or null when it cannot be read. */
async function fetchTagIconByPath(path: string): Promise<string | null> {
  const r = await apiClient.get<{ contentType?: string; data?: string }>('/tag-icons/image', { params: { path } });
  const raw = r.data;
  if (!raw || typeof raw.data !== 'string' || typeof raw.contentType !== 'string') return null;
  try {
    const bytes = Uint8Array.from(atob(raw.data), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: raw.contentType });
    return blob.size === 0 ? null : URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * A library icon by path, cached like useTagIcon: the library rarely changes,
 * so an icon is fetched once per session and reused by the list and picker.
 */
export function useTagIconByPath(path: string | null | undefined): { url: string | null; loading: boolean } {
  const query = useQuery({
    queryKey: ['tag-icon-path', path],
    queryFn: () => fetchTagIconByPath(path as string),
    enabled: !!path,
    staleTime: 60 * 60_000,
    gcTime: 120 * 60_000,
    retry: false,
  });
  return { url: query.data ?? null, loading: query.isLoading && !!path };
}
